import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import {
  ArrowLeft,
  Upload,
  Plus,
  X,
  Search,
  Trash2,
  Save,
  Send,
  ChevronDown,
  Loader2
} from 'lucide-react';
import Layout from './Layout';
import PricingModeSelector from './PricingModeSelector';
import authService from '../services/authService';
import quotationService from '../services/quotationService';
import clientService from '../services/clientService';
import EnhancedProductLookup from './EnhancedProductLookup';
import FileAttachmentManager from './FileAttachmentManager';
import ProductTable from './ProductTable';
import PriceHistoryDrawer from './PriceHistoryDrawer';
import ClientSelector from './ClientSelector';
import SyncProductsButton from './SyncProductsButton';
import { showNotification } from '../utils/notifications';
import useUnsavedChangesWarning from '../hooks/useUnsavedChangesWarning';
import UnsavedChangesDialog from './UnsavedChangesDialog';

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Helper function to generate quotation ID
const generateQuotationId = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const date = String(now.getDate()).padStart(2, '0');
  // Simulate daily counter - in real implementation, this should come from backend
  const dailyCounter = Math.floor(Math.random() * 99) + 1;
  const counterStr = String(dailyCounter).padStart(2, '0');
  return `QT${year}${month}${date}${counterStr}`;
};

const QuotationForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id && id !== 'new');
  const isView = window.location.pathname.includes('/view');

  // Handler for product ID click navigation
  const handleProductIdClick = (product) => {
    console.log('handleProductIdClick called with product:', product);
    // Use product_mongo_id (stored when product is added) for navigation to product edit page
    const productMongoId = product.product_mongo_id || product._id;
    console.log('Extracted productMongoId:', productMongoId);

    if (productMongoId) {
      // Set referrer to quotations since this is QuotationForm
      const targetUrl = `/products/${productMongoId}/edit?referrer=quotations`;
      console.log('Navigating to:', targetUrl);
      navigate(targetUrl);
    } else {
      console.log('No product_mongo_id or _id found - navigation aborted');
      alert('无法找到产品ID，可能产品已被删除');
    }
  };

  // State management
  const [quotationItems, setQuotationItems] = useState([]);
  const [unmatchedItems, setUnmatchedItems] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClientData, setSelectedClientData] = useState(null);
  // Pricing mode: 'tax_exclusive' | 'tax_inclusive'
  const [pricingMode, setPricingMode] = useState('tax_exclusive');
  const [comment, setComment] = useState('');
  const [quotationNumber, setQuotationNumber] = useState(generateQuotationId());

  // International shipping/export control state
  const [shippingType, setShippingType] = useState('domestic'); // 'domestic' or 'international'
  const [destinationCountry, setDestinationCountry] = useState('');
  const [exportComplianceStatus, setExportComplianceStatus] = useState('');



  // Undo/Redo functionality
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // Modal states
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [showCreateProductDialog, setShowCreateProductDialog] = useState(false);
  const [selectedUnmatchedItem, setSelectedUnmatchedItem] = useState(null);
  // Replace product dialog state
  const [replaceDialogState, setReplaceDialogState] = useState({
    open: false,
    replacingIndex: null,
    currentProduct: null
  });

  const [products, ] = useState([]);
  const [, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newProductData, setNewProductData] = useState({
    name: '',
    specifications: '',
    unitPrice: '',
    category: '',
  });

  // Price history drawer states
  const [priceHistoryDrawer, setPriceHistoryDrawer] = useState({
    open: false,
    productId: '',
    productName: '',
    tipDiameter: '',
    totalLength: '',
    category: '',
    lineItemIndex: null // Track the specific line item index to update
  });

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Initialize unsaved changes warning hook
  const unsavedChangesWarning = useUnsavedChangesWarning({
    quotationItems,
    selectedClientId,
    selectedClientData,
    comment,
    quotationNumber,
    shippingType,
    destinationCountry,
    exportComplianceStatus,
    pricingMode
  }, !isView); // Only enable when not in view mode

  // Undo/Redo functionality
  // const saveToUndoStack = React.useCallback(() => {
  //   setUndoStack(prev => [...prev.slice(-9), [...quotationItems]]); // Keep last 10 states
  //   setRedoStack([]); // Clear redo stack when new action is performed
  // }, [quotationItems]);

  const handleUndo = React.useCallback(() => {
    if (undoStack.length === 0) return;

    const previousState = undoStack[undoStack.length - 1];
    setRedoStack(prev => [...prev, [...quotationItems]]);
    setUndoStack(prev => prev.slice(0, -1));
    setQuotationItems(previousState);
    showNotification('撤销成功', 'info');
  }, [undoStack, quotationItems]);

  const handleRedo = React.useCallback(() => {
    if (redoStack.length === 0) return;

    const nextState = redoStack[redoStack.length - 1];
    setUndoStack(prev => [...prev, [...quotationItems]]);
    setRedoStack(prev => prev.slice(0, -1));
    setQuotationItems(nextState);
    showNotification('重做成功', 'info');
  }, [redoStack, quotationItems]);

  // Keyboard shortcuts for undo/redo
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };

    if (!isView) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [handleUndo, handleRedo, isView]);

  // Load products on component mount
  useEffect(() => {
    if (id && id !== 'new') {
      loadQuotation(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);


  // Handle client selection
  const handleClientChange = (clientId, clientData) => {
    setSelectedClientId(clientId);
    setSelectedClientData(clientData);
  };


  // const loadProducts = async () => {
  //   try {
  //     const response = await productService.getProducts(1, 100);
  //     setProducts(response.products || []);
  //   } catch (err) {
  //     console.error('Failed to load products:', err);
  //   }
  // };


  const loadQuotation = async (quotationId) => {
    try {
      setLoading(true);
      const quotation = await quotationService.getQuotation(quotationId);

      // Check client access for confidentiality
      const isAdmin = authService.isAdmin();
      if (quotation.client_id && !isAdmin) {
        try {
          // Fetch the client to check if it's confidential
          const clientResponse = await clientService.getClient(quotation.client_id);
          const client = clientResponse.client || clientResponse;

          if (client?.is_confidential) {
            setError('Access denied: This quotation belongs to a confidential client');
            showNotification('无权访问：此报价单属于保密客户', 'error');
            setTimeout(() => navigate('/quotations'), 2000);
            return;
          }
        } catch (err) {
          console.warn('Could not verify client access:', err);
          // Continue loading if we can't verify (backend will enforce)
        }
      }

      // Initialize pricing mode if present
      if (quotation.pricing_mode) {
        setPricingMode(quotation.pricing_mode);
      }

      // Handle client data properly
      if (quotation.client_id) {
        // Real client ID - let ClientSelector handle loading
        setSelectedClientId(quotation.client_id);
        setSelectedClientData(null); // Will be loaded by ClientSelector
      } else if (quotation.client_name) {
        // Legacy data - try to find matching client first
        try {
          const searchResult = await quotationService.getClientNames();
          const matchingClient = searchResult.client_names?.find(name =>
            name.toLowerCase() === quotation.client_name.toLowerCase()
          );

          if (matchingClient) {
            // Found matching client name, but we need to get full client data
            // For now, create a legacy client data structure
            setSelectedClientId('legacy-' + quotationId);
            setSelectedClientData({
              name: quotation.client_name,
              client_name: quotation.client_name,
              isLegacy: true
            });
          } else {
            // No match found - create legacy entry
            setSelectedClientId('legacy-' + quotationId);
            setSelectedClientData({
              name: quotation.client_name,
              client_name: quotation.client_name,
              isLegacy: true
            });
          }
        } catch (error) {
          // Fallback to legacy client data
          setSelectedClientId('legacy-' + quotationId);
          setSelectedClientData({
            name: quotation.client_name,
            client_name: quotation.client_name,
            isLegacy: true
          });
        }
      } else {
        // No client data at all
        setSelectedClientId(null);
        setSelectedClientData(null);
      }

      setComment(quotation.comment || '');
      setQuotationNumber(quotation.quotation_id || `QT-${new Date().getFullYear()}-${quotationId}`);

      // Load international shipping data
      setShippingType(quotation.shipping_type || 'domestic');
      setDestinationCountry(quotation.destination_country || '');
      setExportComplianceStatus(quotation.export_compliance_status || '');

      setQuotationItems(quotation.products?.map(p => ({
        ...p,
        _id: `${p.product_id}-${p.name}-${p.specification}-${p.client_ref_number}`,
        product_mongo_id: p.product_mongo_id || p._id, // Preserve MongoDB product ID for navigation
        quantity: p.quantity || 1,
        price: p.price || 0,
        unit_price_tax_included: p.unit_price_tax_included || (p.price ? p.price * 1.13 : 0)
      })) || []);

      // Update initial state for unsaved changes tracking after loading
      setTimeout(() => {
        const loadedProducts = quotation.products?.map(p => ({
          ...p,
          _id: `${p.product_id}-${p.name}-${p.specification}-${p.client_ref_number}`,
          product_mongo_id: p.product_mongo_id || p._id, // Preserve MongoDB product ID for navigation
          quantity: p.quantity || 1,
          price: p.price || 0,
          unit_price_tax_included: p.unit_price_tax_included || (p.price ? p.price * 1.13 : 0)
        })) || [];

        unsavedChangesWarning.updateInitialState({
          quotationItems: loadedProducts,
          selectedClientId: quotation.client_id || ('legacy-' + quotationId),
          selectedClientData: quotation.client_id ? null : (quotation.client_name ? {
            name: quotation.client_name,
            client_name: quotation.client_name,
            isLegacy: true
          } : null),
          comment: quotation.comment || '',
          quotationNumber: quotation.quotation_id || `QT-${new Date().getFullYear()}-${quotationId}`,
          shippingType: quotation.shipping_type || 'domestic',
          destinationCountry: quotation.destination_country || '',
          exportComplianceStatus: quotation.export_compliance_status || '',
          pricingMode: quotation.pricing_mode || 'tax_exclusive'
        });
      }, 100); // Small delay to ensure state updates have propagated
    } catch (err) {
      setError('Failed to load quotation');
    } finally {
      setLoading(false);
    }
  };


  // Compute totals respecting per-line tax indicator or document-level pricing mode
  const computeTotals = React.useCallback((items) => {
    let subtotalExcl = 0;
    let totalIncl = 0;

    items.forEach((item) => {
      const qty = item.quantity || 0;
      const rate = typeof item.tax_rate === 'number' ? item.tax_rate : 0.13;
      const effectiveMode = item.pricing_mode || pricingMode;

      // Prefer API-provided values
      const unitInclRaw = (typeof item.unit_price_tax_included === 'number')
        ? item.unit_price_tax_included
        : (typeof item.tax_inclusive_price === 'number')
          ? item.tax_inclusive_price
          : (effectiveMode === 'tax_inclusive' && typeof item.price === 'number')
            ? item.price
            : undefined;

      const unitExclRaw = (typeof item.tax_exclusive_price === 'number')
        ? item.tax_exclusive_price
        : (effectiveMode === 'tax_inclusive' && typeof unitInclRaw === 'number')
          ? (unitInclRaw / (1 + rate))
          : (typeof item.price === 'number' ? item.price : undefined);

      const unitIncl = typeof unitInclRaw === 'number'
        ? unitInclRaw
        : (typeof unitExclRaw === 'number' ? unitExclRaw * (1 + rate) : 0);

      const unitExcl = typeof unitExclRaw === 'number'
        ? unitExclRaw
        : (typeof unitInclRaw === 'number' ? unitInclRaw / (1 + rate) : 0);

      // Keep unit prices unrounded, but round line item totals to 2 decimal places
      const lineExcl = (unitExcl || 0) * qty;
      const lineIncl = (unitIncl || 0) * qty;

      subtotalExcl += Math.round(lineExcl * 100) / 100;
      totalIncl += Math.round(lineIncl * 100) / 100;
    });

    // Tax amount as difference to match backend semantics, rounded to 2 decimal places
    const taxAmount = Math.round((totalIncl - subtotalExcl) * 100) / 100;
    return { subtotalExcl: Math.round(subtotalExcl * 100) / 100, taxAmount, totalIncl: Math.round(totalIncl * 100) / 100 };
  }, [pricingMode]);

  const { subtotalExcl, taxAmount, totalIncl } = computeTotals(quotationItems);

  // Auto-save functionality for draft quotations
  const autoSaveDraft = React.useCallback(() => {
    const debouncedSave = debounce(async () => {
      if (!isEdit || !id || quotationItems.length === 0) return;

      try {
        const { subtotalExcl: sub, taxAmount: tax, totalIncl: total } = computeTotals(quotationItems);

        const quotationData = {
          client_name: selectedClientData?.name || selectedClientData?.client_name || '',
          products: quotationItems.map(item => ({
            product_id: item.product_id,
            name: item.name,
            specification: item.specification,
            category: item.category,
            quantity: item.quantity || 1,
            price: item.price || 0,
            unit_price_tax_included: item.unit_price_tax_included || (item.price || 0) * 1.13,
            // Manufacturing parameters
            material: item.material,
            coating: item.coating,
            diameter: item.diameter,
            length: item.length,
            shank_diameter: item.shank_diameter,
            tolerance: item.tolerance,
            blade_type: item.blade_type,
            guide_pillar_length: item.guide_pillar_length,
            guide_pillar_tolerance: item.guide_pillar_tolerance,
            marking: item.marking
          })),
          comment: comment,
          status: 'draft',
          pricing_mode: pricingMode,
          subtotal: sub,
          tax_rate: 0.13,
          tax_amount: tax,
          total_price: total,
          // International trading data
          shipping_type: shippingType,
          destination_country: shippingType === 'international' ? destinationCountry : null,
          export_compliance_status: shippingType === 'international' ? exportComplianceStatus : null,
          is_international_trade: shippingType === 'international'
        };

        await quotationService.updateQuotation(id, quotationData);
        console.log('✅ Auto-saved draft quotation');
      } catch (error) {
        console.error('Auto-save failed:', error);
      }
    }, 2000);

    debouncedSave();
  }, [isEdit, id, quotationItems, selectedClientData, comment, shippingType, destinationCountry, exportComplianceStatus, pricingMode, computeTotals]);

  // Auto-save when quotation items change
  useEffect(() => {
    if (isEdit && quotationItems.length > 0) {
      autoSaveDraft();
    }
  }, [quotationItems, selectedClientData, comment, isEdit, autoSaveDraft]);


  // const calculateTaxInclusivePrice = (price) => {
  //   const TAX_RATE = 0.13; // 13% tax rate
  //   return price * (1 + TAX_RATE);
  // };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };


  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      setIsUploading(true);

      // Simulate Excel parsing
      setTimeout(() => {
        const mockUnmatched = [
          {
            rowIndex: 1,
            productName: 'Custom Steel Component',
            code: 'CSC001',
            quantity: 10,
            specifications: 'Custom specification 1'
          },
          {
            rowIndex: 3,
            productName: 'Special Aluminum Part',
            code: 'SAP001',
            quantity: 5,
            specifications: 'Special aluminum part for industrial use'
          },
        ];
        setUnmatchedItems(mockUnmatched);

        // Add matched products (using first product as example)
        if (products.length > 0) {
          const matchedItem = {
            ...products[0],
            quantity: 20,
            price: 500 // Default price for matched item
          };
          setQuotationItems(prev => [...prev, matchedItem]);
        }
        setIsUploading(false);
        setIsUploadModalOpen(false);
      }, 1500);
    }
  };


  const handleAddProducts = (products) => {
    const newItems = products.map(product => {
      // Check for existing product with same product_id, specification, AND client_ref_number
      // Products with same product_id but different specification are treated as separate line items
      const existingItem = quotationItems.find(item =>
        item.product_id === product.product_id &&
        item.specification === product.specification &&
        (item.client_ref_number || '') === (product.client_ref_number || '')
      );
      if (existingItem) {
        // Update existing item quantity - use the composite key for identification
        const itemKey = existingItem._id;
        updateQuantity(itemKey, existingItem.quantity + 1);
        return null;
      } else {
        return {
          ...product,
          product_mongo_id: product._id, // Store MongoDB _id for navigation
          quantity: 1,
          price: 0, // Default price, user needs to set
          unit_price_tax_included: 0 // Initialize tax-inclusive price
        };
      }
    }).filter(Boolean);

    if (newItems.length > 0) {
      setQuotationItems(prev => [...prev, ...newItems]);
    }
  };

  const updateQuantity = (productId, newQuantity) => {
    setQuotationItems(items =>
      items.map(item =>
        item._id === productId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };



  const removeItem = (productId) => {
    setQuotationItems(items => items.filter(item => item._id !== productId));
  };

  // Handle product replacement
  const handleReplaceProduct = (index, currentProduct) => {
    setReplaceDialogState({
      open: true,
      replacingIndex: index,
      currentProduct: currentProduct
    });
  };

  const handleReplaceConfirm = (selectedProducts) => {
    if (selectedProducts.length === 0) return;

    const newProduct = selectedProducts[0]; // Take first selected product
    const updatedItems = [...quotationItems];
    const oldQuantity = quotationItems[replaceDialogState.replacingIndex].quantity;

    // Replace product while preserving quantity
    updatedItems[replaceDialogState.replacingIndex] = {
      ...newProduct,
      _id: `${newProduct.product_id}-${newProduct.name}-${newProduct.specification}`,
      product_mongo_id: newProduct._id,
      quantity: oldQuantity, // Preserve original quantity
      price: newProduct.price || 0,
      unit_price_tax_included: newProduct.price ? newProduct.price * 1.13 : 0
    };

    setQuotationItems(updatedItems);
    setReplaceDialogState({ open: false, replacingIndex: null, currentProduct: null });
  };

  // Handle product reordering from drag and drop
  const handleProductReorder = (reorderedProducts) => {
    setQuotationItems(reorderedProducts);
  };

  const handleCreateProduct = () => {
    if (!selectedUnmatchedItem || !newProductData.name || !newProductData.unitPrice) return;

    const newProduct = {
      _id: `temp_${Date.now()}`,
      product_id: selectedUnmatchedItem.code || `CODE${Date.now()}`,
      name: newProductData.name,
      specification: newProductData.specifications,
      category: newProductData.category,
    };

    // Add to quotation items
    const newItem = {
      ...newProduct,
      quantity: selectedUnmatchedItem.quantity,
      price: parseFloat(newProductData.unitPrice) || 0,
    };

    setQuotationItems(prev => [...prev, newItem]);
    setUnmatchedItems(prev => prev.filter(item => item.rowIndex !== selectedUnmatchedItem.rowIndex));
    setShowCreateProductDialog(false);
    setSelectedUnmatchedItem(null);
    setNewProductData({ name: '', specifications: '', unitPrice: '', category: '' });
  };

  const handleBulkAdd = () => {
    const newItems = unmatchedItems.map(item => ({
      _id: `temp_${Date.now()}_${item.rowIndex}`,
      product_id: item.code || `CODE${Date.now()}_${item.rowIndex}`,
      name: item.productName,
      specification: item.specifications || '',
      category: 'Unknown',
      quantity: item.quantity,
      price: 50.00, // Default price
    }));

    setQuotationItems(prev => [...prev, ...newItems]);
    setUnmatchedItems([]);
  };

  const dismissUnmatched = (rowIndex) => {
    setUnmatchedItems(items => items.filter(item => item.rowIndex !== rowIndex));
  };

  const handleSaveDraft = async () => {
    try {
      unsavedChangesWarning.setIsSubmitting(true); // Mark as submitting
      const { subtotalExcl: sub, taxAmount: tax, totalIncl: total } = computeTotals(quotationItems);

      const quotationData = {
        quotation_id: quotationNumber,
        client_name: selectedClientData?.name || selectedClientData?.client_name || '',
        client_id: selectedClientId,
                  products: quotationItems.map(item => {
                    let unitExclusive;
                    let unitInclusive;

                    if (pricingMode === 'tax_inclusive') {
                      unitInclusive = item.unit_price_tax_included ?? ((item.price ?? 0) * 1.13);
                      unitExclusive = unitInclusive / 1.13;
                    } else { // pricingMode === 'tax_exclusive'
                      unitExclusive = item.price ?? 0;
                      unitInclusive = unitExclusive * 1.13;
                    }

                    // Unit prices are NOT rounded, only totals are rounded
                    const lineItemTotal = (item.quantity || 0) * (pricingMode === 'tax_inclusive' ? unitInclusive : unitExclusive);
                    const lineItemTotalRounded = Math.round(lineItemTotal * 100) / 100;

                    return {
                      _id: item.product_mongo_id || item._id, // Include MongoDB _id for product reference
                      product_id: item.product_id,
                      name: item.name,
                      specification: item.specification,
                      client_ref_number: item.client_ref_number,
                      category: item.category,
                      quantity: item.quantity || 1,
                      price: unitExclusive,
                      unit_price: unitExclusive,
                      unit_price_tax_included: unitInclusive,
                      total_price: lineItemTotalRounded,
                      // Manufacturing parameters
                      material: item.material,
                      coating: item.coating,
                      diameter: item.diameter,
                      length: item.length,
                      shank_diameter: item.shank_diameter,
                      tolerance: item.tolerance,
                      blade_type: item.blade_type,
                      guide_pillar_length: item.guide_pillar_length,
                      guide_pillar_tolerance: item.guide_pillar_tolerance,
                      marking: item.marking
                    };
                  }),
                comment: comment,
                status: 'draft',        pricing_mode: pricingMode,
        subtotal: sub,
        tax_rate: 0.13,
        tax_amount: tax,
        total_price: total,
        // International trading data
        shipping_type: shippingType,
        destination_country: shippingType === 'international' ? destinationCountry : null,
        export_compliance_status: shippingType === 'international' ? exportComplianceStatus : null,
        is_international_trade: shippingType === 'international'
      };

      if (isEdit) {
        await quotationService.updateQuotation(id, quotationData);
      } else {
        await quotationService.createQuotation(quotationData);
      }

      unsavedChangesWarning.resetDirty(); // Reset dirty state after successful save
      alert('Quotation saved as draft');
      navigate('/quotations');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save quotation');
      unsavedChangesWarning.setIsSubmitting(false); // Reset submitting state on error
    }
  };

  const handleSubmitQuotation = async () => {
    if (unmatchedItems.length > 0) {
      alert('Please resolve all unmatched items before submitting');
      return;
    }
    if (quotationItems.length === 0) {
      alert('Please add at least one item to the quotation');
      return;
    }
    if (!selectedClientId || (!selectedClientData?.name && !selectedClientData?.client_name)) {
      alert('Please select a client');
      return;
    }

    try {
      unsavedChangesWarning.setIsSubmitting(true); // Mark as submitting
      const { subtotalExcl: sub, taxAmount: tax, totalIncl: total } = computeTotals(quotationItems);

      const quotationData = {
        quotation_id: quotationNumber,
        client_name: selectedClientData?.name || selectedClientData?.client_name || '',
        client_id: selectedClientId,
                  products: quotationItems.map(item => {
                    let unitExclusive;
                    let unitInclusive;

                    if (pricingMode === 'tax_inclusive') {
                      unitInclusive = item.unit_price_tax_included ?? ((item.price ?? 0) * 1.13);
                      unitExclusive = unitInclusive / 1.13;
                    } else { // pricingMode === 'tax_exclusive'
                      unitExclusive = item.price ?? 0;
                      unitInclusive = unitExclusive * 1.13;
                    }

                    // Unit prices are NOT rounded, only totals are rounded
                    const lineItemTotal = (item.quantity || 0) * (pricingMode === 'tax_inclusive' ? unitInclusive : unitExclusive);
                    const lineItemTotalRounded = Math.round(lineItemTotal * 100) / 100;

                    return {
                      _id: item.product_mongo_id || item._id, // Include MongoDB _id for product reference
                      product_id: item.product_id,
                      name: item.name,
                      specification: item.specification,
                      client_ref_number: item.client_ref_number,
                      category: item.category,
                      quantity: item.quantity || 1,
                      price: unitExclusive,
                      unit_price: unitExclusive,
                      unit_price_tax_included: unitInclusive,
                      total_price: lineItemTotalRounded,
                      // Manufacturing parameters
                      material: item.material,
                      coating: item.coating,
                      diameter: item.diameter,
                      length: item.length,
                      shank_diameter: item.shank_diameter,
                      tolerance: item.tolerance,
                      blade_type: item.blade_type,
                      guide_pillar_length: item.guide_pillar_length,
                      guide_pillar_tolerance: item.guide_pillar_tolerance,
                      marking: item.marking
                    };
                  }),        comment: comment,
        status: 'pending',
        pricing_mode: pricingMode,
        subtotal: sub,
        tax_rate: 0.13,
        tax_amount: tax,
        total_price: total,
        // International trading data
        shipping_type: shippingType,
        destination_country: shippingType === 'international' ? destinationCountry : null,
        export_compliance_status: shippingType === 'international' ? exportComplianceStatus : null,
        is_international_trade: shippingType === 'international'
      };

      if (isEdit) {
        await quotationService.updateQuotation(id, quotationData);
      } else {
        await quotationService.createQuotation(quotationData);
      }

      unsavedChangesWarning.resetDirty(); // Reset dirty state after successful save
      alert('Quotation submitted successfully!');
      navigate('/quotations');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit quotation');
      unsavedChangesWarning.setIsSubmitting(false); // Reset submitting state on error
    }
  };

  // Price history handlers
  const openPriceHistoryDrawer = (index, product) => {
    // Use index to track the specific line item
    if (!product || !product.product_id) {
      setError('无法查询价格历史：产品编号缺失');
      return;
    }

    setPriceHistoryDrawer({
      open: true,
      productId: product.product_id || '',
      productName: product.name || '',
      tipDiameter: product.tip_diameter || '',
      totalLength: product.total_length || '',
      category: product.category || '',
      lineItemIndex: index // Store the line item index for correct price application
    });
  };

  const handleDeleteQuotation = async () => {
    try {
      unsavedChangesWarning.setIsSubmitting(true); // Mark as submitting to prevent warnings
      await quotationService.deleteQuotation(id);
      alert('Quotation deleted successfully!');
      navigate('/quotations');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete quotation');
    } finally {
      setDeleteConfirmOpen(false);
    }
  };


  return (
    <Layout title={isView ? '查看报价单' : isEdit ? '编辑报价单' : '新建报价单'} activeTab="quotations">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                if (isView) {
                  navigate('/quotations');
                } else {
                  unsavedChangesWarning.checkUnsavedChanges(() => navigate('/quotations'));
                }
              }}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">
                {isView ? '查看报价单' : isEdit ? '编辑报价单' : '新建报价单'}
              </h1>
              <p className="text-muted-foreground">
                报价单：{quotationNumber}
              </p>
            </div>
          </div>

          {/* Add Product Dropdown */}
          {!isView && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  添加产品
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setIsUploadModalOpen(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  批量上传（Excel）
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsSearchModalOpen(true)}>
                  <Search className="h-4 w-4 mr-2" />
                  手动查找
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" onClick={() => setError('')}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Client Information */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>客户信息</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>报价单编号</Label>
                <Input
                  value={quotationNumber}
                  onChange={(e) => setQuotationNumber(e.target.value)}
                  placeholder="输入报价单编号"
                />
                <p className="text-xs text-muted-foreground">可自定义报价单编号</p>
              </div>
              <div className="space-y-2">
                <ClientSelector
                  selectedClientId={selectedClientId}
                  onClientChange={handleClientChange}
                  selectedClientName={selectedClientData?.name || selectedClientData?.client_name || ''}
                  disabled={isView}
                  required
                  label="客户名称"
                  placeholder="搜索或选择客户"
                  helperText="选择客户后将自动填充相关信息"
                  size="medium"
                  compact={false}
                />
              </div>
              <div className="space-y-2">
                <Label>报价日期</Label>
                <Input
                  type="date"
                  defaultValue={new Date().toISOString().split('T')[0]}
                  disabled={isView}
                />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label>备注</Label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="补充说明..."
                  disabled={isView}
                  rows={3}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Unmatched Items Banner */}
        {unmatchedItems.length > 0 && !isView && (
          <Alert className="mb-6 border-warning/20 bg-warning/10">
            <AlertTitle className="flex justify-between items-center">
              <span className="text-lg font-semibold">
                在 Excel 文件中发现 {unmatchedItems.length} 个未匹配条目
              </span>
              <Button size="sm" onClick={handleBulkAdd}>
                <Plus className="h-4 w-4 mr-2" />
                批量全部添加
              </Button>
            </AlertTitle>
            <AlertDescription>
              <div className="mt-4 space-y-2">
                {unmatchedItems.map((item) => (
                  <div
                    key={item.rowIndex}
                    className="flex items-center justify-between p-4 bg-card border border-warning/20 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.productName}</span>
                        {item.code && (
                          <Badge variant="outline">{item.code}</Badge>
                        )}
                        <span className="text-sm text-muted-foreground">
                          数量：{item.quantity}
                        </span>
                      </div>
                      {item.specifications && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {item.specifications}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedUnmatchedItem(item);
                          setNewProductData({
                            name: item.productName,
                            specifications: item.specifications || '',
                            unitPrice: '',
                            category: '',
                          });
                          setShowCreateProductDialog(true);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        创建产品
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => alert('Search existing functionality')}
                      >
                        <Search className="h-4 w-4 mr-1" />
                        搜索已存在
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => dismissUnmatched(item.rowIndex)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Quotation Items Table - Main Focus */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>报价条目</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Total Price above table */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex gap-4 items-center">
                <PricingModeSelector
                  value={pricingMode}
                  onChange={setPricingMode}
                  disabled={isView}
                  variant="compact"
                  size="small"
                />
                {isEdit && quotationItems.length > 0 && !isView && (
                  <SyncProductsButton
                    onPreview={async () => {
                      // Preview changes (NO database modification)
                      const preview = await quotationService.previewSyncQuotationProducts(id);
                      return preview;
                    }}
                    onApply={async () => {
                      // Apply changes after user approval
                      const result = await quotationService.syncQuotationProducts(id);
                      // Reload the quotation to show updated products
                      if (result.updated_count > 0) {
                        await loadQuotation(id);
                      }
                      return result;
                    }}
                    variant="outlined"
                    size="small"
                  />
                )}
              </div>
              <div className="text-right">
                <p className="text-xl text-muted-foreground">
                  小计：{formatCurrency(subtotalExcl)}
                </p>
                <p className="text-sm text-muted-foreground">
                  税额（13%）：{formatCurrency(taxAmount)}
                </p>
                <p className="text-2xl font-bold text-primary">
                  合计：{formatCurrency(totalIncl)}
                </p>
              </div>
            </div>

            <ProductTable
              products={quotationItems}
              view="quotation"
              onEdit={isView ? undefined : (product) => {
                console.log('Edit product:', product);
              }}
              onDelete={isView ? undefined : removeItem}
              onReorder={isView ? undefined : handleProductReorder}
              onPriceChange={isView ? undefined : (index, newPrice) => {
                const updatedItems = [...quotationItems];
                const priceWithTax = newPrice * 1.13; // Calculate tax inclusive price (no rounding on unit price)
                updatedItems[index] = {
                  ...updatedItems[index],
                  price: newPrice,
                  unit_price_tax_included: priceWithTax,
                  pricing_mode: 'tax_exclusive'
                };
                setQuotationItems(updatedItems);
              }}
              onPriceTaxIncludedChange={isView ? undefined : (index, newPriceWithTax) => {
                const priceExcludingTax = newPriceWithTax / 1.13; // Remove 13% tax (no rounding on unit price)
                const updatedItems = [...quotationItems];
                updatedItems[index] = {
                  ...updatedItems[index],
                  price: priceExcludingTax,
                  unit_price_tax_included: newPriceWithTax,
                  pricing_mode: 'tax_inclusive'
                };
                setQuotationItems(updatedItems);
              }}
              onQuantityChange={isView ? undefined : (index, newQuantity) => {
                const updatedItems = [...quotationItems];
                updatedItems[index] = { ...updatedItems[index], quantity: newQuantity };
                setQuotationItems(updatedItems);
              }}
              onPriceLookup={isView ? undefined : (index, product) => {
                console.log('Price lookup for product:', product, 'at index:', index);
                // Use index to track the specific line item for price application
                openPriceHistoryDrawer(index, product);
              }}
              onReplace={isView ? undefined : handleReplaceProduct}
              onProductIdClick={handleProductIdClick}
              readOnly={isView}
              title="报价产品列表"
            />
          </CardContent>
        </Card>

        {/* File Attachments Section - Show for edit mode only */}
        {isEdit && id && (
          <FileAttachmentManager
            entityType="quotations"
            entityId={id}
            title="报价单附件"
            className="mt-6"
          />
        )}

        {/* Action Buttons */}
        {!isView && (
          <div className="flex justify-between items-center gap-4 pt-4 border-t">
            {isEdit && (
              <Button
                variant="destructive"
                onClick={() => setDeleteConfirmOpen(true)}
                size="lg"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                删除报价单
              </Button>
            )}
            <div className="flex gap-4 ml-auto">
              <Button
                variant="outline"
                onClick={handleSaveDraft}
                size="lg"
              >
                <Save className="h-4 w-4 mr-2" />
                保存为草稿
              </Button>
              <Button
                onClick={handleSubmitQuotation}
                disabled={quotationItems.length === 0 || unmatchedItems.length > 0}
                size="lg"
              >
                <Send className="h-4 w-4 mr-2" />
                提交报价单
              </Button>
            </div>
          </div>
        )}

        {/* Excel Upload Modal */}
        <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                上传 Excel 文件
              </DialogTitle>
            </DialogHeader>
            <div
              className={cn(
                "border-2 border-dashed border-primary rounded-lg p-8 text-center cursor-pointer transition-colors",
                "bg-muted/50 hover:bg-muted"
              )}
              onClick={() => !isUploading && document.getElementById('excel-upload-modal').click()}
            >
              <Upload className="h-12 w-12 mx-auto text-primary mb-4" />
              <p className="text-lg font-medium mb-1">
                {isUploading ? '处理中...' : '上传买方 Excel 文件'}
              </p>
              <p className="text-sm text-muted-foreground">
                产品将自动匹配
              </p>
              {isUploading && <Loader2 className="h-6 w-6 animate-spin mx-auto mt-4" />}
              <input
                id="excel-upload-modal"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
            {uploadedFile && (
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <p className="font-medium text-sm">
                  已上传文件：{uploadedFile.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isUploading ? '处理中...' : '处理完成'}
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Enhanced Product Search Modal */}
        <EnhancedProductLookup
          open={isSearchModalOpen}
          onClose={() => setIsSearchModalOpen(false)}
          onAddProducts={handleAddProducts}
          clientName={selectedClientData?.name || selectedClientData?.client_name || ''}
          currentQuotationItems={quotationItems}
        />

        {/* Replace Product Dialog */}
        <EnhancedProductLookup
          open={replaceDialogState.open}
          onClose={() => setReplaceDialogState({ open: false, replacingIndex: null, currentProduct: null })}
          onAddProducts={handleReplaceConfirm}
          clientName={selectedClientData?.name || selectedClientData?.client_name || ''}
          currentQuotationItems={quotationItems}
        />

        {/* Create Product Dialog */}
        <Dialog open={showCreateProductDialog} onOpenChange={setShowCreateProductDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                新建产品 - {selectedUnmatchedItem?.code}
              </DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>产品名称 *</Label>
                <Input
                  value={newProductData.name}
                  onChange={(e) => setNewProductData({...newProductData, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>规格</Label>
                <Textarea
                  value={newProductData.specifications}
                  onChange={(e) => setNewProductData({...newProductData, specifications: e.target.value})}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>单价 *</Label>
                  <Input
                    type="number"
                    value={newProductData.unitPrice}
                    onChange={(e) => setNewProductData({...newProductData, unitPrice: e.target.value})}
                    step="0.01"
                    min="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>分类</Label>
                  <Input
                    value={newProductData.category}
                    onChange={(e) => setNewProductData({...newProductData, category: e.target.value})}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateProductDialog(false)}>
                取消
              </Button>
              <Button
                onClick={handleCreateProduct}
                disabled={!newProductData.name || !newProductData.unitPrice}
              >
                创建产品
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Price History Drawer */}
        <PriceHistoryDrawer
          open={priceHistoryDrawer.open}
          onClose={() => setPriceHistoryDrawer({ open: false, productId: '', productName: '', tipDiameter: '', totalLength: '', category: '', lineItemIndex: null })}
          initialMode="single"
          productId={priceHistoryDrawer.productId}
          productName={priceHistoryDrawer.productName}
          initialTipDiameter={priceHistoryDrawer.tipDiameter}
          initialTotalLength={priceHistoryDrawer.totalLength}
          initialCategory={priceHistoryDrawer.category}
          initialClientName={selectedClientData?.name || selectedClientData?.client_name || ''}
          showActions={true}
          onApplyPrice={(priceData) => {
            // Use lineItemIndex to update the correct line item (handles same product_id with different specs)
            const itemIndex = priceHistoryDrawer.lineItemIndex;
            if (itemIndex !== null && itemIndex >= 0 && itemIndex < quotationItems.length) {
              const updatedItems = [...quotationItems];
              // Determine prices based on selected mode
              const exclusive = (typeof priceData === 'number')
                ? priceData
                : (
                    priceData?.tax_exclusive_price
                    ?? priceData?.price
                    ?? priceData?.base_price
                    ?? (
                      typeof priceData === 'object' && typeof priceData?.tax_inclusive_price === 'number'
                        ? (priceData.tax_inclusive_price / 1.13)
                        : 0
                    )
                  );
              const inclusive = (typeof priceData === 'number')
                ? (priceData * 1.13)
                : (
                    priceData?.tax_inclusive_price
                    ?? (
                      typeof priceData === 'object' && typeof priceData?.tax_exclusive_price === 'number'
                        ? (priceData.tax_exclusive_price * 1.13)
                        : 0
                    )
                  );

              updatedItems[itemIndex] = {
                ...updatedItems[itemIndex],
                price: exclusive,
                unit_price_tax_included: inclusive || (exclusive * 1.13),
                pricing_mode: pricingMode
              };
              setQuotationItems(updatedItems);
            }
          }}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>删除报价单</DialogTitle>
            </DialogHeader>
            <p>
              确定要删除报价单 {quotationNumber} 吗？此操作不可撤销。
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
                取消
              </Button>
              <Button variant="destructive" onClick={handleDeleteQuotation}>
                删除
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Unsaved Changes Warning Dialog */}
        <UnsavedChangesDialog
          open={unsavedChangesWarning.showDialog}
          onSaveAndLeave={() => {
            if (selectedClientId && selectedClientData && quotationItems.length > 0) {
              unsavedChangesWarning.handleSaveAndLeave(handleSubmitQuotation);
            } else {
              unsavedChangesWarning.handleSaveAndLeave(handleSaveDraft);
            }
          }}
          onDiscardChanges={unsavedChangesWarning.handleDiscardChanges}
          onCancel={unsavedChangesWarning.handleCancel}
          title="未保存的报价单"
          message="您有未保存的报价单更改。离开此页面将丢失这些更改。"
        />
      </div>
    </Layout>
  );
};

export default QuotationForm;
